import React from 'react'
import { Typography, useTheme, useMediaQuery } from '@mui/material'
import { AdminPropertyAd } from 'types'
import {
  EstatePropertyCommercialExtrasEnum,
  EstatePropertyExtrasEnum,
  EstatePropertyLandExtrasEnum,
  PropertyAttributeEnum,
} from 'types/enums'
import { FLOORS } from 'helpers/constants'
import { sortBy } from 'lodash'
import OverflowTypography from 'shared/OverflowTypography'
import { useTranslation } from 'react-i18next'

const MAX_WIDTH = '10ch'
const MAX_WIDTH_TABLET = '8ch'
const MAX_WIDTH_EXPANDED = '60px'
interface Props {
  propertyAd: AdminPropertyAd
  isExpanded: boolean
}

export const GetBedrooms = ({
  propertyAd,
}: Props): React.ReactElement | null => {
  const BEDROOMS = propertyAd?.properties?.[0]?.propertyAttributes?.find(
    (item) =>
      item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_BEDROOMS
  )?.valueNumber
  if (BEDROOMS == null) return <>{`-`}</>

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {BEDROOMS}
      </Typography>
    </>
  )
}

export const GetEstateType = ({
  propertyAd,
  isExpanded,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const theme = useTheme()
  const matchesTablet = useMediaQuery(theme.breakpoints.between(600, 900))
  const ESTATE_TYPE = propertyAd?.properties?.[0]?.propertyAttributes?.find(
    (item) =>
      item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_TYPE
  )?.valueText
  if (ESTATE_TYPE == null) return <>{`-`}</>
  const isExpandedFallback =
    !isExpanded && matchesTablet ? MAX_WIDTH_TABLET : MAX_WIDTH
  return (
    <>
      <OverflowTypography
        maxWidth={isExpanded ? MAX_WIDTH_EXPANDED : isExpandedFallback}
        label={
          t(
            `enums.ESTATE_PROPERTY_TYPE.${ESTATE_TYPE}.base`
          ) as unknown as string
        }
        textAlign={'center'}
      >
        {
          t(
            `enums.ESTATE_PROPERTY_TYPE.${ESTATE_TYPE}.base`
          ) as unknown as string
        }
      </OverflowTypography>
    </>
  )
}

export const GetBathroomsWcs = ({ propertyAd }: Props): React.ReactElement => {
  const BATHROOMS = propertyAd?.properties?.[0]?.propertyAttributes?.find(
    (item) =>
      item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_BATHROOMS
  )?.valueNumber
  const WCS = propertyAd?.properties?.[0]?.propertyAttributes?.find(
    (item) => item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_WC
  )?.valueNumber

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {`${BATHROOMS ?? '-'}`}
        {WCS ? `+${WCS}` : ''}
      </Typography>
    </>
  )
}

export const GetFireplaces = ({
  propertyAd,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()

  const FIREPLACE_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find(
      (item) =>
        item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_EXTRAS &&
        (item.valueText === EstatePropertyExtrasEnum.Fireplace ||
          item.valueText === EstatePropertyExtrasEnum.FireplaceNo)
    )
  if (FIREPLACE_ATTRIBUTE == null || FIREPLACE_ATTRIBUTE.valueText == null)
    return <>{`-`}</>

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {t(
          `tagResponses.${
            FIREPLACE_ATTRIBUTE.valueText === EstatePropertyExtrasEnum.Fireplace
              ? 'yes'
              : 'no'
          }`
        )}
      </Typography>
    </>
  )
}

export const GetElevator = ({
  propertyAd,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const IS_ESTATE = [1, 4].includes(propertyAd.adCategory.id)
  const ELEVATOR_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find((item) =>
      IS_ESTATE
        ? item.type.description ===
            PropertyAttributeEnum.ESTATE_PROPERTY_EXTRAS &&
          (item.valueText === EstatePropertyExtrasEnum.Elevator ||
            item.valueText === EstatePropertyExtrasEnum.ElevatorNo)
        : item.type.description ===
            PropertyAttributeEnum.ESTATE_PROPERTY_COMMERCIAL_EXTRAS &&
          (item.valueText === EstatePropertyCommercialExtrasEnum.Elevator ||
            item.valueText === EstatePropertyCommercialExtrasEnum.ElevatorNo)
    )

  if (ELEVATOR_ATTRIBUTE == null || ELEVATOR_ATTRIBUTE.valueText == null)
    return <>{`-`}</>

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {t(
          `tagResponses.${
            ELEVATOR_ATTRIBUTE.valueText === EstatePropertyExtrasEnum.Elevator
              ? 'yes'
              : 'no'
          }`
        )}
      </Typography>
    </>
  )
}

export const GetEstateParking = ({
  propertyAd,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const ESTATE_PARKING_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find(
      (item) =>
        item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_EXTRAS &&
        (item.valueText === EstatePropertyExtrasEnum.EstateParking ||
          item.valueText === EstatePropertyExtrasEnum.EstateParkingNo)
    )
  if (
    ESTATE_PARKING_ATTRIBUTE == null ||
    ESTATE_PARKING_ATTRIBUTE.valueText == null
  )
    return <>{`-`}</>

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {t(
          `tagResponses.${
            ESTATE_PARKING_ATTRIBUTE.valueText ===
            EstatePropertyExtrasEnum.EstateParking
              ? 'yes'
              : 'no'
          }`
        )}
      </Typography>
    </>
  )
}

export const GetCommercialParking = ({
  propertyAd,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()

  const COMMERCIAL_PARKING_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find(
      (item) =>
        item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_COMMERCIAL_EXTRAS &&
        (item.valueText === EstatePropertyCommercialExtrasEnum.EstateParking ||
          item.valueText === EstatePropertyCommercialExtrasEnum.EstateParkingNo)
    )
  if (
    COMMERCIAL_PARKING_ATTRIBUTE == null ||
    COMMERCIAL_PARKING_ATTRIBUTE.valueText == null
  )
    return <>{`-`}</>

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {t(
          `tagResponses.${
            COMMERCIAL_PARKING_ATTRIBUTE.valueText ===
            EstatePropertyCommercialExtrasEnum.EstateParking
              ? 'yes'
              : 'no'
          }`
        )}
      </Typography>
    </>
  )
}

export const GetCompensation = ({
  propertyAd,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const COMPENSATION_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find(
      (item) =>
        item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_LAND_EXTRAS &&
        (item.valueText === EstatePropertyLandExtrasEnum.Compensation ||
          item.valueText === EstatePropertyLandExtrasEnum.CompensationNo)
    )
  if (
    COMPENSATION_ATTRIBUTE == null ||
    COMPENSATION_ATTRIBUTE.valueText == null
  )
    return <>{`-`}</>

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {t(
          `tagResponses.${
            COMPENSATION_ATTRIBUTE.valueText ===
            EstatePropertyLandExtrasEnum.Compensation
              ? 'yes'
              : 'no'
          }`
        )}
      </Typography>
    </>
  )
}
export const GetWithBuilding = ({
  propertyAd,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const WITH_BUILDING_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find(
      (item) =>
        item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_LAND_EXTRAS &&
        (item.valueText === EstatePropertyLandExtrasEnum.WithBuilding ||
          item.valueText === EstatePropertyLandExtrasEnum.WithBuildingNo)
    )
  if (
    WITH_BUILDING_ATTRIBUTE == null ||
    WITH_BUILDING_ATTRIBUTE.valueText == null
  )
    return <>{`-`}</>

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {t(
          `tagResponses.${
            WITH_BUILDING_ATTRIBUTE.valueText ===
            EstatePropertyLandExtrasEnum.WithBuilding
              ? 'yes'
              : 'no'
          }`
        )}
      </Typography>
    </>
  )
}

export const GetPropertyAdExternalId = ({
  propertyAd,
  isExpanded,
}: Props): React.ReactElement => {
  const theme = useTheme()
  const matchesTablet = useMediaQuery(theme.breakpoints.between(600, 900))
  const isExpandedFallback =
    !isExpanded && matchesTablet ? MAX_WIDTH_TABLET : MAX_WIDTH
  return (
    <>
      <OverflowTypography
        maxWidth={isExpanded ? MAX_WIDTH_EXPANDED : isExpandedFallback}
        label={propertyAd.externalId ?? '-'}
        textAlign={'center'}
      >
        {propertyAd.externalId ?? '-'}
      </OverflowTypography>
    </>
  )
}

export const GetEnergyClass = ({
  propertyAd,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const ENERGY_CLASS_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find(
      (item) =>
        item.type.description ===
        PropertyAttributeEnum.ESTATE_PROPERTY_ENERGY_CLASS
    )

  if (
    ENERGY_CLASS_ATTRIBUTE == null ||
    ENERGY_CLASS_ATTRIBUTE?.valueText == null
  )
    return <>{`-`}</>

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {t(
          `enums.ESTATE_PROPERTY_ENERGY_CLASS.${ENERGY_CLASS_ATTRIBUTE?.valueText}.base`
        )}
      </Typography>
    </>
  )
}

export const GetFurnished = ({
  propertyAd,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const FURNISHED_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find(
      (item) =>
        item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_EXTRAS &&
        (item.valueText === EstatePropertyExtrasEnum.Furnished ||
          item.valueText === EstatePropertyExtrasEnum.FurnishedNo)
    )
  if (FURNISHED_ATTRIBUTE == null || FURNISHED_ATTRIBUTE.valueText == null)
    return <>{`-`}</>

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {t(
          `tagResponses.${
            FURNISHED_ATTRIBUTE.valueText === EstatePropertyExtrasEnum.Furnished
              ? 'yes'
              : 'no'
          }`
        )}
      </Typography>
    </>
  )
}
export const GetAccess = ({
  propertyAd,
  isExpanded,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const theme = useTheme()
  const matchesTablet = useMediaQuery(theme.breakpoints.between(600, 900))
  const ACCESS_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find(
      (item) =>
        item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_ACCESS
    )

  if (ACCESS_ATTRIBUTE == null || ACCESS_ATTRIBUTE?.valueText == null)
    return <>{`-`}</>
  const isExpandedFallback =
    !isExpanded && matchesTablet ? MAX_WIDTH_TABLET : MAX_WIDTH
  return (
    <>
      <OverflowTypography
        maxWidth={isExpanded ? MAX_WIDTH_EXPANDED : isExpandedFallback}
        label={t(
          `enums.ESTATE_PROPERTY_ACCESS.${ACCESS_ATTRIBUTE?.valueText}.base`
        )}
        textAlign={'center'}
      >
        {t(`enums.ESTATE_PROPERTY_ACCESS.${ACCESS_ATTRIBUTE?.valueText}.base`)}
      </OverflowTypography>
    </>
  )
}

export const GetHeat = ({
  propertyAd,
  isExpanded,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const theme = useTheme()
  const matchesTablet = useMediaQuery(theme.breakpoints.between(600, 900))
  const HEAT_ATTRIBUTE = propertyAd?.properties?.[0]?.propertyAttributes?.find(
    (item) =>
      item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_HEAT
  )

  if (HEAT_ATTRIBUTE == null || HEAT_ATTRIBUTE?.valueText == null)
    return <>{`-`}</>
  const isExpandedFallback =
    !isExpanded && matchesTablet ? MAX_WIDTH_TABLET : MAX_WIDTH
  return (
    <>
      <OverflowTypography
        maxWidth={isExpanded ? MAX_WIDTH_EXPANDED : isExpandedFallback}
        label={t(
          `enums.ESTATE_PROPERTY_HEAT.${HEAT_ATTRIBUTE?.valueText}.base`
        )}
        textAlign={'center'}
      >
        {t(`enums.ESTATE_PROPERTY_HEAT.${HEAT_ATTRIBUTE?.valueText}.base`)}
      </OverflowTypography>
    </>
  )
}
export const GetRenovationYear = ({
  propertyAd,
}: Props): React.ReactElement | null => {
  const RENOVATION_YEAR_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find(
      (item) =>
        item.type.description ===
        PropertyAttributeEnum.ESTATE_PROPERTY_RENOVATION_YEAR
    )

  if (
    RENOVATION_YEAR_ATTRIBUTE == null ||
    RENOVATION_YEAR_ATTRIBUTE?.valueNumber == null
  )
    return <>{`-`}</>

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {RENOVATION_YEAR_ATTRIBUTE?.valueNumber}
      </Typography>
    </>
  )
}

export const ParseFloors = ({
  propertyAd,
  isExpanded,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const theme = useTheme()
  const matchesTablet = useMediaQuery(theme.breakpoints.between(600, 900))

  const FLOOR_ATTRIBUTES =
    propertyAd?.properties?.[0]?.propertyAttributes?.filter(
      (item) =>
        item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_FLOOR
    )

  if (!FLOOR_ATTRIBUTES?.length) return <>{`-`}</>

  const FLOOR_VALUES = FLOOR_ATTRIBUTES?.map((item) => item.valueText)

  const FILTERED_FLOORS = FLOORS.filter((item) =>
    FLOOR_VALUES.includes(item.value)
  )

  const SORTED = sortBy(FILTERED_FLOORS, 'order')
  const isExpandedFallback =
    !isExpanded && matchesTablet ? MAX_WIDTH_TABLET : MAX_WIDTH
  return (
    <>
      <OverflowTypography
        maxWidth={isExpanded ? MAX_WIDTH_EXPANDED : isExpandedFallback}
        label={
          <Typography
            component="div"
            fontWeight={'inherit'}
            fontSize="inherit"
            sx={{ whiteSpace: 'pre-line' }}
          >
            {SORTED.map(
              (item, index) =>
                `${t(`enums.ESTATE_PROPERTY_FLOOR.${item.value}.base`)}${
                  index === 0 ? '' : ', '
                }`
            )}
          </Typography>
        }
      >
        {SORTED?.length
          ? SORTED.map(
              (item) => `${t(`enums.ESTATE_PROPERTY_FLOOR.${item.value}.base`)}`
            )
          : '-'}
      </OverflowTypography>
    </>
  )
}

export const GetSpaces = ({ propertyAd }: Props): React.ReactElement | null => {
  const SPACES_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find(
      (item) =>
        item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_SPACES
    )

  if (SPACES_ATTRIBUTE == null || SPACES_ATTRIBUTE?.valueNumber == null)
    return <>{`-`}</>

  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {SPACES_ATTRIBUTE?.valueNumber ?? '-'}
      </Typography>
    </>
  )
}

export const GetConstructionYear = ({
  propertyAd,
}: Props): React.ReactElement => {
  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {propertyAd?.properties?.[0]?.constructionYear ?? `-`}
      </Typography>
    </>
  )
}

// LAND ATTRS

export const GetBuildFactor = ({ propertyAd }: Props): React.ReactElement => {
  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {propertyAd?.properties?.[0]?.buildFactor ?? `-`}
      </Typography>
    </>
  )
}
export const GetCoverageFactor = ({
  propertyAd,
  isExpanded,
}: Props): React.ReactElement => {
  return (
    <>
      <Typography component="div" fontWeight={'inherit'} fontSize="inherit">
        {propertyAd?.properties?.[0]?.coverageFactor ?? `-`}
      </Typography>
    </>
  )
}

export const GetPlans = ({
  propertyAd,
  isExpanded,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const theme = useTheme()
  const matchesTablet = useMediaQuery(theme.breakpoints.between(600, 900))
  const CITY_PLAN_ATTRIBUTE =
    propertyAd?.properties?.[0]?.propertyAttributes?.find(
      (item) =>
        item.type.description ===
        PropertyAttributeEnum.ESTATE_LAND_PROPERTY_CITY_PLAN
    )

  if (CITY_PLAN_ATTRIBUTE == null || CITY_PLAN_ATTRIBUTE?.valueText == null)
    return <>{`-`}</>

  const isExpandedFallback =
    !isExpanded && matchesTablet ? MAX_WIDTH_TABLET : MAX_WIDTH
  return (
    <>
      <OverflowTypography
        maxWidth={isExpanded ? MAX_WIDTH_EXPANDED : isExpandedFallback}
        label={t(
          `enums.ESTATE_LAND_PROPERTY_CITY_PLAN.${CITY_PLAN_ATTRIBUTE?.valueText}.base`
        )}
        textAlign={'center'}
      >
        {t(
          `enums.ESTATE_LAND_PROPERTY_CITY_PLAN.${CITY_PLAN_ATTRIBUTE?.valueText}.base`
        )}
      </OverflowTypography>
    </>
  )
}

export const GetDimensions = ({
  propertyAd,
  isExpanded,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const theme = useTheme()
  const matchesTablet = useMediaQuery(theme.breakpoints.between(600, 900))
  const PROPERTY_WIDTH = propertyAd?.properties?.[0]?.propertyAttributes?.find(
    (item) =>
      item.type.description ===
      PropertyAttributeEnum.ESTATE_LAND_PROPERTY_PLOT_WIDTH
  )
  const PROPERTY_LENGTH = propertyAd?.properties?.[0]?.propertyAttributes?.find(
    (item) =>
      item.type.description ===
      PropertyAttributeEnum.ESTATE_LAND_PROPERTY_PLOT_LENGTH
  )

  if (PROPERTY_WIDTH == null || PROPERTY_WIDTH?.valueNumber == null)
    return <>{`-`}</>
  if (PROPERTY_LENGTH == null || PROPERTY_LENGTH?.valueNumber == null)
    return <>{`-`}</>
  const isExpandedFallback =
    !isExpanded && matchesTablet ? MAX_WIDTH_TABLET : MAX_WIDTH
  return (
    <>
      <OverflowTypography
        maxWidth={isExpanded ? MAX_WIDTH_EXPANDED : isExpandedFallback}
        label={`${t(`generics.area`, {
          count: PROPERTY_WIDTH?.valueNumber || 0,
        })}-${t(`generics.area`, {
          count: PROPERTY_LENGTH?.valueNumber || 0,
        })}`}
        textAlign={'center'}
      >
        {`${t(`generics.area`, {
          count: PROPERTY_WIDTH?.valueNumber || 0,
        })}-${t(`generics.area`, {
          count: PROPERTY_LENGTH?.valueNumber || 0,
        })}`}
      </OverflowTypography>
    </>
  )
}

export const GetPricePerBuildableArea = ({
  propertyAd,
  isExpanded,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const theme = useTheme()
  const matchesTablet = useMediaQuery(theme.breakpoints.between(600, 900))
  const isExpandedFallback =
    !isExpanded && matchesTablet ? MAX_WIDTH_TABLET : MAX_WIDTH
  return (
    <>
      <OverflowTypography
        maxWidth={isExpanded ? MAX_WIDTH_EXPANDED : isExpandedFallback}
        label={
          propertyAd?.salesPricePerBuildableSqrMeter != null
            ? t(`generics.currency`, {
                count: propertyAd?.salesPricePerBuildableSqrMeter,
              })
            : `-`
        }
        textAlign={'center'}
      >
        {propertyAd?.salesPricePerBuildableSqrMeter != null
          ? t(`generics.currency`, {
              count: Number(
                propertyAd?.salesPricePerBuildableSqrMeter.toFixed(2)
              ),
            })
          : `-`}
      </OverflowTypography>
    </>
  )
}

export const GetUsage = ({
  propertyAd,
  isExpanded,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const theme = useTheme()
  const matchesTablet = useMediaQuery(theme.breakpoints.between(600, 900))
  const USAGE_ATTRIBUTE = propertyAd?.properties?.[0]?.propertyAttributes?.find(
    (item) =>
      item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_USAGE
  )

  if (USAGE_ATTRIBUTE == null || USAGE_ATTRIBUTE?.valueText == null)
    return <>{`-`}</>

  return (
    <>
      <OverflowTypography
        maxWidth={
          isExpanded
            ? MAX_WIDTH_EXPANDED
            : !isExpanded && matchesTablet
            ? MAX_WIDTH_TABLET
            : MAX_WIDTH
        }
        label={t(
          `enums.ESTATE_PROPERTY_USAGE.${USAGE_ATTRIBUTE?.valueText}.base`
        )}
        textAlign={'center'}
      >
        {t(`enums.ESTATE_PROPERTY_USAGE.${USAGE_ATTRIBUTE?.valueText}.base`)}
      </OverflowTypography>
    </>
  )
}
